// ===============================================================
// V·IANet Requests (M16) — AprobadoresTab: Config AprobadorConfigurado
// ===============================================================

import { useState, useEffect, useMemo } from 'react';
import { useI18n } from '../../../../i18n';
import { request } from '../../../shared/services/api';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { useRequestsConfig } from '../hooks/useRequestsConfig';
import type { AprobadorConfigurado, TipoSolicitud, SimpleResponse } from '../types/requests.types';
import { C } from '../../../../lib/design';

type FuenteResolucion = AprobadorConfigurado['fuenteResolucion'];

const FUENTES: { value: FuenteResolucion; label: string }[] = [
  { value: 'MANUAL', label: 'Manual (persona)' },
  { value: 'PROYECTO', label: 'Proyecto' },
  { value: 'UMBRAL_IMPORTE', label: 'Umbral de importe' },
  { value: 'JERARQUIA', label: 'Jerarquia' },
  { value: 'ROL_GOBERNANZA', label: 'Rol de gobernanza' },
];

interface PersonaOption {
  id: string;
  nombre: string;
  apellido: string;
}

interface AprobadorForm {
  fuenteResolucion: FuenteResolucion;
  personaId: string;
  importeMinimo: string;
  orden: number;
  obligatorio: boolean;
  rolAprobacion: string;
}

const EMPTY_FORM: AprobadorForm = {
  fuenteResolucion: 'MANUAL',
  personaId: '',
  importeMinimo: '',
  orden: 1,
  obligatorio: true,
  rolAprobacion: '',
};

export function AprobadoresTab() {
  const { t } = useI18n();

  const [tipos, setTipos] = useState<TipoSolicitud[]>([]);
  const [personas, setPersonas] = useState<PersonaOption[]>([]);
  const [selectedTipoId, setSelectedTipoId] = useState('');

  const queryParams = useMemo(
    () => (selectedTipoId ? { tipoSolicitudId: selectedTipoId } : undefined),
    [selectedTipoId],
  );

  const { items, loading, crear, actualizar, eliminar, refetch } =
    useRequestsConfig<AprobadorConfigurado>('/requests/aprobadores', {
      autoFetch: !!selectedTipoId,
      queryParams,
    });

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<AprobadorForm>(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Fetch tipos & personas
  useEffect(() => {
    const fetchAux = async () => {
      try {
        const [tRes, pRes] = await Promise.all([
          request<SimpleResponse<TipoSolicitud>>('/requests/tipos'),
          request<SimpleResponse<PersonaOption>>('/apps/personas'),
        ]);
        setTipos(tRes.data);
        setPersonas(pRes.data);
      } catch {
        // silent
      }
    };
    fetchAux();
  }, []);

  // Refetch when tipo changes
  useEffect(() => {
    if (selectedTipoId) refetch();
  }, [selectedTipoId, refetch]);

  const sortedItems = useMemo(() => [...items].sort((a, b) => a.orden - b.orden), [items]);

  const openCreate = () => {
    const nextOrden = sortedItems.length > 0 ? Math.max(...sortedItems.map((i) => i.orden)) + 1 : 1;
    setEditId(null);
    setForm({ ...EMPTY_FORM, orden: nextOrden });
    setShowModal(true);
  };

  const openEdit = (ap: AprobadorConfigurado) => {
    setEditId(ap.id);
    setForm({
      fuenteResolucion: ap.fuenteResolucion,
      personaId: ap.personaId ?? '',
      importeMinimo: ap.importeMinimo != null ? String(ap.importeMinimo) : '',
      orden: ap.orden,
      obligatorio: ap.obligatorio,
      rolAprobacion: ap.rolAprobacion ?? '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!selectedTipoId) return;
    try {
      const payload = {
        tipoSolicitudId: selectedTipoId,
        fuenteResolucion: form.fuenteResolucion,
        personaId: form.fuenteResolucion === 'MANUAL' && form.personaId ? form.personaId : null,
        importeMinimo:
          form.fuenteResolucion === 'UMBRAL_IMPORTE' && form.importeMinimo
            ? Number(form.importeMinimo)
            : null,
        orden: form.orden,
        obligatorio: form.obligatorio,
        rolAprobacion: form.rolAprobacion || null,
      };
      if (editId) {
        await actualizar(editId, payload as unknown as Partial<AprobadorConfigurado>);
      } else {
        await crear(payload as unknown as Partial<AprobadorConfigurado>);
      }
      closeModal();
    } catch {
      // error handled by hook
    }
  };

  const handleReorder = async (ap: AprobadorConfigurado, direction: 'up' | 'down') => {
    const idx = sortedItems.findIndex((i) => i.id === ap.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const swapItem = sortedItems[swapIdx];
    if (!swapItem) return;

    try {
      await Promise.all([
        actualizar(ap.id, { orden: swapItem.orden } as Partial<AprobadorConfigurado>),
        actualizar(swapItem.id, { orden: ap.orden } as Partial<AprobadorConfigurado>),
      ]);
    } catch {
      // error handled by hook
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await eliminar(confirmDeleteId);
    } catch {
      // error handled by hook
    }
    setConfirmDeleteId(null);
  };

  const canSave =
    form.fuenteResolucion &&
    (form.fuenteResolucion !== 'MANUAL' || form.personaId) &&
    (form.fuenteResolucion !== 'UMBRAL_IMPORTE' || form.importeMinimo);

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
          {t.requests?.aprobadores ?? 'Aprobadores configurados'}
        </h3>
        {selectedTipoId && (
          <button
            onClick={openCreate}
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: 'none',
              background: '#1F3864',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t.requests?.nuevoAprobador ?? 'Añadir aprobador'}
          </button>
        )}
      </div>

      {/* Tipo selector */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
          {t.requests?.seleccionarTipo ?? 'Tipo de solicitud'}
          <select
            value={selectedTipoId}
            onChange={(e) => setSelectedTipoId(e.target.value)}
            style={{
              display: 'block',
              width: '100%',
              maxWidth: 400,
              marginTop: 4,
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #D1D5DB',
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          >
            <option value="">Seleccionar tipo...</option>
            {tipos.map((tp) => (
              <option key={tp.id} value={tp.id}>
                {tp.codigo} - {tp.nombre}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Content */}
      {!selectedTipoId ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.gray400 }}>
          {t.requests?.seleccionaTipoMsg ??
            'Selecciona un tipo de solicitud para configurar sus aprobadores'}
        </div>
      ) : loading ? (
        <LoadingSpinner size="md" muted />
      ) : sortedItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.gray400 }}>
          {t.requests?.sinAprobadores ?? 'No hay aprobadores configurados para este tipo'}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
              <th
                style={{
                  textAlign: 'center',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.gray500,
                  width: 60,
                }}
              >
                Orden
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.gray500,
                }}
              >
                Fuente
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.gray500,
                }}
              >
                Persona / Detalle
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.gray500,
                }}
              >
                Importe min.
              </th>
              <th
                style={{
                  textAlign: 'center',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.gray500,
                }}
              >
                Obligatorio
              </th>
              <th
                style={{
                  textAlign: 'center',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.gray500,
                }}
              >
                Activo
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.gray500,
                }}
              >
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((ap, idx) => (
              <tr key={ap.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                    }}
                  >
                    <button
                      onClick={() => handleReorder(ap, 'up')}
                      disabled={idx === 0}
                      title="Subir"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: idx === 0 ? 'not-allowed' : 'pointer',
                        padding: 2,
                        opacity: idx === 0 ? 0.3 : 1,
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M7 3v8M4 6l3-3 3 3"
                          stroke="#2F5496"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <span
                      style={{ fontSize: 14, fontWeight: 500, minWidth: 20, textAlign: 'center' }}
                    >
                      {ap.orden}
                    </span>
                    <button
                      onClick={() => handleReorder(ap, 'down')}
                      disabled={idx === sortedItems.length - 1}
                      title="Bajar"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: idx === sortedItems.length - 1 ? 'not-allowed' : 'pointer',
                        padding: 2,
                        opacity: idx === sortedItems.length - 1 ? 0.3 : 1,
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M7 11V3M4 8l3 3 3-3"
                          stroke="#2F5496"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13 }}>
                  <span
                    style={{
                      background: fuenteColor(ap.fuenteResolucion).bg,
                      color: fuenteColor(ap.fuenteResolucion).text,
                      padding: '2px 8px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {FUENTES.find((f) => f.value === ap.fuenteResolucion)?.label ??
                      ap.fuenteResolucion}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 14 }}>
                  {ap.persona
                    ? `${ap.persona.nombre} ${ap.persona.apellido}`
                    : (ap.rolAprobacion ?? '-')}
                </td>
                <td
                  style={{
                    padding: '12px 16px',
                    fontSize: 14,
                    textAlign: 'right',
                    fontFamily: 'monospace',
                  }}
                >
                  {ap.importeMinimo != null ? `${ap.importeMinimo.toLocaleString()} EUR` : '-'}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  {ap.obligatorio ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8l3.5 3.5L13 5"
                        stroke="#10B981"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <span style={{ color: '#D1D5DB' }}>-</span>
                  )}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      display: 'inline-block',
                      background: ap.activo ? '#10B981' : '#D1D5DB',
                    }}
                  />
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                  <button
                    onClick={() => openEdit(ap)}
                    title={t.requests?.editar ?? 'Editar'}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 4,
                      marginRight: 4,
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z"
                        stroke="#2F5496"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(ap.id)}
                    title={t.requests?.eliminar ?? 'Eliminar'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 4h10M5.5 4V3a1 1 0 011-1h3a1 1 0 011 1v1M6 7v5M10 7v5M4 4l.7 9a1 1 0 001 .9h4.6a1 1 0 001-.9L12 4"
                        stroke="#DC2626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              width: 520,
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            <h4 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              {editId
                ? (t.requests?.editar ?? 'Editar aprobador')
                : (t.requests?.nuevoAprobador ?? 'Añadir aprobador')}
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                Fuente de resolucion *
                <select
                  value={form.fuenteResolucion}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      fuenteResolucion: e.target.value as FuenteResolucion,
                      personaId: e.target.value !== 'MANUAL' ? '' : f.personaId,
                      importeMinimo: e.target.value !== 'UMBRAL_IMPORTE' ? '' : f.importeMinimo,
                    }))
                  }
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    boxSizing: 'border-box',
                  }}
                >
                  {FUENTES.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </label>

              {form.fuenteResolucion === 'MANUAL' && (
                <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                  Persona *
                  <select
                    value={form.personaId}
                    onChange={(e) => setForm((f) => ({ ...f, personaId: e.target.value }))}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 4,
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #D1D5DB',
                      fontSize: 14,
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">Seleccionar persona...</option>
                    {personas.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nombre} {p.apellido}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {form.fuenteResolucion === 'UMBRAL_IMPORTE' && (
                <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                  Importe minimo (EUR) *
                  <input
                    type="number"
                    value={form.importeMinimo}
                    onChange={(e) => setForm((f) => ({ ...f, importeMinimo: e.target.value }))}
                    placeholder="0.00"
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 4,
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #D1D5DB',
                      fontSize: 14,
                      boxSizing: 'border-box',
                    }}
                  />
                </label>
              )}

              <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                Rol de aprobacion
                <input
                  value={form.rolAprobacion}
                  onChange={(e) => setForm((f) => ({ ...f, rolAprobacion: e.target.value }))}
                  placeholder="Ej: RESPONSABLE_AREA"
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    boxSizing: 'border-box',
                  }}
                />
              </label>

              <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                Orden
                <input
                  type="number"
                  value={form.orden}
                  onChange={(e) => setForm((f) => ({ ...f, orden: Number(e.target.value) }))}
                  min={1}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    boxSizing: 'border-box',
                  }}
                />
              </label>

              <label
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: C.gray700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={form.obligatorio}
                  onChange={(e) => setForm((f) => ({ ...f, obligatorio: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: '#1F3864' }}
                />
                Obligatorio
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={closeModal}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.cancelar ?? 'Cancelar'}
              </button>
              <button
                onClick={handleSave}
                disabled={!canSave}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: canSave ? '#1F3864' : C.gray400,
                  color: '#fff',
                  cursor: canSave ? 'pointer' : 'not-allowed',
                }}
              >
                {t.common.guardar}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 400 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              {t.requests?.confirmarEliminar ?? 'Confirmar eliminacion'}
            </h4>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: C.gray500 }}>
              {t.requests?.confirmarEliminarMsg ?? 'Esta accion no se puede deshacer.'}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteId(null)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.cancelar ?? 'Cancelar'}
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#DC2626',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.eliminar ?? 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -- Helpers --

function fuenteColor(fuente: FuenteResolucion): { bg: string; text: string } {
  switch (fuente) {
    case 'MANUAL':
      return { bg: '#EEF2FF', text: '#4338CA' };
    case 'PROYECTO':
      return { bg: '#F0FDF4', text: '#166534' };
    case 'UMBRAL_IMPORTE':
      return { bg: '#FFFBEB', text: '#92400E' };
    case 'JERARQUIA':
      return { bg: '#FFF1F2', text: '#9F1239' };
    case 'ROL_GOBERNANZA':
      return { bg: '#F0F9FF', text: '#0C4A6E' };
    default:
      return { bg: '#F3F4F6', text: C.gray700 };
  }
}
